/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash/fp';
import glamorous from 'glamorous';
import { load as loadWebFonts } from 'webfontloader';
import Responsive from 'app/components/responsive/Responsive';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import { GOLF } from 'app/constants/challengeOneSportIds';
import {
  fetchPlayerCompetitionData,
  fetchPlayerGameData,
  fetchPlayerSeasonData,
  fetchTeamCompetitionData
} from '../../api/onesport';
import AnimatedGroup from '../../components/commons/AnimatedGroup';
import decodePlayerCompetitionStats from '../../normalizers/decodePlayerCompetitionStats';
import decodePlayerSeasonStats from '../../normalizers/decodePlayerSeasonStats';
import decodeTeamCompetitionStats from '../../normalizers/decodeTeamCompetitionStats';
import decodeRacingCompetitionStats from '../../normalizers/decodeRacingCompetitionStats';
import OneScoreWidgetCore from './OneScoreWidgetCore';

type State = {
  data: ?Object,
  error: ?string,
  isLoading: boolean
};

loadWebFonts({
  google: {
    families: ['Roboto']
  }
});

class OneScoreWidget extends React.Component<Props, State> {
  props: Props;

  state: State = {
    data: undefined,
    error: undefined,
    isLoading: false
  };

  wrapper: HTMLElement; // eslint-disable-line react/sort-comp

  static defaultProps = {
    isRacing: false,
    renderEntity: () => null
  };

  componentDidMount() {
    const { isOpen } = this.props;

    if (isOpen) {
      this.handleFetch();
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { isOpen } = this.props;

    if (!prevProps.isOpen && isOpen) {
      this.handleFetch();
    }
  }

  close = () => {
    const { onClose, id } = this.props;
    this.wrapper.style['pointer-events'] = 'none';
    onClose(id);
  };

  handleFetch() {
    const { competitionId, entityId, gameId, isGolf, isRacing, isTeam, sportId } = this.props;
    let fetchData = isTeam ? fetchTeamCompetitionData : fetchPlayerCompetitionData;
    let decodeData = isTeam ? decodeTeamCompetitionStats : decodePlayerCompetitionStats;

    const gameOrCompetitionId = isRacing ? gameId : competitionId;

    if (isRacing) {
      fetchData = fetchPlayerGameData;
      decodeData = decodeRacingCompetitionStats;
    } else if (isGolf) {
      fetchData = fetchPlayerSeasonData;
      decodeData = decodePlayerSeasonStats;
    }

    this.setState({
      error: null,
      isLoading: true
    });

    fetchData(sportId, entityId, gameOrCompetitionId)
      .then(res => {
        this.setState({
          data: decodeData(res.data),
          isLoading: false
        });
      })
      .catch(() => {
        this.setState({
          isLoading: false,
          error: 'No record found'
        });
      });
  }

  render() {
    const { renderEntity, isGolf, isOpen, isRacing } = this.props;
    const { data, error, isLoading } = this.state;

    return (
      <div>
        <Responsive tabletLandscape desktop>
          {isLargeScreen =>
            isLargeScreen ? (
              <React.Fragment>
                {isOpen && (
                  <OneScoreWidgetCore
                    player={data}
                    isGolf={isGolf}
                    isLoading={isLoading}
                    isRacing={isRacing}
                    error={error}
                  />
                )}
              </React.Fragment>
            ) : (
              <AnimatedGroup
                component={ModalInnerWrapper}
                onEnter={Enter.slideUp(0.5)}
                onLeave={Leave.slideDown({
                  duration: 0.5,
                  onComplete: () => {
                    // console.log('onComplete');
                  }
                })}
              >
                {isOpen && (
                  <ModalInner
                    key="modal-inner"
                    innerRef={ref => {
                      this.wrapper = ref;
                    }}
                  >
                    <ModalHead>
                      <CloseButton
                        onClick={() => {
                          this.close();
                        }}
                      />
                      {renderEntity()}
                    </ModalHead>
                    <ModalContent>
                      <OneScoreWidgetCore
                        player={data}
                        isGolf={isGolf}
                        isLoading={isLoading}
                        isRacing={isRacing}
                        error={error}
                      />
                    </ModalContent>
                  </ModalInner>
                )}
              </AnimatedGroup>
            )
          }
        </Responsive>
      </div>
    );
  }
}

const connector = connect((state, { id, isTeam }) => {
  const entity = isTeam ? state.challenge.teams[id] : state.challenge.players[id];

  return {
    competitionId: get('onesportCompetitionId', entity),
    gameId: get('onesportGameId', entity),
    entityId: get('onesportEntityId', entity),
    sportId: state.challenge.onesportSportId,
    isGolf: state.challenge.onesportSportId === GOLF
  };
}, {});

export default connector(OneScoreWidget);

interface Props {
  competitionId: string;
  entityId: string;
  gameId: string;
  id: string;
  isGolf: boolean;
  isOpen: boolean;
  isRacing?: boolean;
  isTeam: boolean;
  onClose: (id: string) => void;
  renderEntity?: () => React.Node;
  sportId: string;
}

const ModalInner = glamorous.div(({ theme }) => ({
  backgroundColor: 'white',
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  left: 0,
  position: 'fixed',
  top: '90px',
  width: '100vw',
  zIndex: theme.zIndex.modal
}));

const ModalInnerWrapper = glamorous.div();

const ModalHead = glamorous.div({
  flexBasis: 'auto',
  flexGrow: 0,
  flexShrink: 0
});

const ModalContent = glamorous.div({
  display: 'flex',
  flexBasis: 0,
  flexDirection: 'column',
  flexGrow: 1,
  flexShrink: 1
});

const CloseButton = glamorous('button', {
  forwardProps: ['onClick'],
  withProps: {
    type: 'button'
  }
})(
  ({ theme }) => ({
    backgroundColor: theme.color.white,
    borderTopLeftRadius: 6,
    borderTopRightRadius: 6,
    color: theme.color.basis4,
    fontSize: theme.fontSize.large,
    height: 60,
    marginTop: 10,
    outline: 'none',
    textTransform: 'uppercase',
    width: '100%'
  }),
  'ion-ios-arrow-down'
);
